import 'numeral/locales';
import * as Sentry from '@sentry/browser';
import axios from 'axios';
import config from '@/config';
import numeral from 'numeral';

export function formatNumeral(value, decimal = 2) {
  const n = numeral();
  n.set(value);

  numeral.locale('nl-nl');

  if (decimal === 0) {
    return n.format('0,0');
  } else if (decimal === 2) {
    return n.format('0,0.00');
  } else if (decimal === 4) {
    return n.format('0,0.0000');
  } else {
    return n.format('0,0.000');
  }
}

/**
 * @param {number} number
 * @param {number} decimals
 * @returns {number}
 */
export function roundFloat(number, decimals = 2) {
  return parseFloat(Number(number).toFixed(decimals));
}

export function percentageDifference(first, second, precision = 2) {
  if (first === 0) {
    return 0;
  }

  const diff = ((second * 100) / first) - 100;
  return diff.toFixed(precision);
}

export function ucfirst(string) {
  return string[0].toUpperCase() + string.slice(1);
}

export function toUpperCase(string) {
  return string.toUpperCase();
}

export function replaceAll(str, find, replace) {
  return str.replace(new RegExp(find, 'g'), replace);
}

export function arrayUnique(value, index, self) {
  return self.indexOf(value) === index;
}

export function arrayShuffle(array) {
  return array.sort(() => 0.5 - Math.random());
}

export function arrayFlat(array) {
  return [].concat(...array);
}

/**
 * Trim given value from start and end of array
 *
 * @param {array} array
 * @param {*} value
 * @returns {array}
 */
export function arrayTrim(array, value = null) {
  // Remove values from start of array
  const firstValueIndex = array.findIndex((i) => i !== value);
  array.splice(0, firstValueIndex);

  // Remove values from end of array
  return array.reduce((result, current, index, source) => {
    if (current !== value) {
      result.push(current);
    } else if (source.slice(index).some((item) => item !== value)) {
      result.push(current);
    }
    return result;
  }, []);
}

/**
 * Import view using lazy load
 */
export function view(v) {
  // eslint-disable-next-line prefer-template
  return () => import(/* webpackChunkName: "view" */ './views/' + v + '.vue');
}

/**
 * Check if is laravel error
 *
 * @param  {Error}  e
 * @return {Boolean}
 */
export function isLaravelError(e) {
  return e && e.response && e.response.data && e.response.data.errors;
}

/**
 * Get laravel error messages
 *
 * @param  {Error} e
 * @return {String}
 */
export function handleLaravelErrors(e) {
  if (axios.isCancel(e)) {
    console.warn('Request canceled');
    return;
  }

  const user = undefined;

  const sentryData = { user };

  if (e && e.response && e.response.data && e.response.data.message) {
    logException(e, { ...sentryData });
    return e.response.data.message;
  }

  if (!isLaravelError(e)) {
    logException(e, { ...sentryData });
    return 'Oops... something went wrong';
  }

  logException(e, { level: 'debug', ...sentryData });

  return Object.values(e.response.data.errors).join('<br>');
}

export const logException = (e, options = {}) => {
  if (axios.isCancel(e)) {
    return;
  }

  if (config.env !== 'production') {
    console.error(e);
  }

  if (options && options.user) {
    Sentry.configureScope((scope) => scope.setUser({ ...options.user }));
  }

  Sentry.captureException(e, { level: 'error' });
};

/**
 * Remove all characters that are not a number
 *
 * @param  {String} value
 * @return {String}
 */
export function onlyNumbers(value) {
  if (!value || typeof value !== 'string') {return '';}

  return value.replace(/[^0-9]/g, '');
}

/**
 * Debounce callback
 *
 * @param  {Function} cb callback
 * @param  {Number}   wait Milliseconds to wait
 */
export function debounce(cb, wait) {
  let timeout;

  return function() {
    const context = this;
    const args = arguments;

    const later = () => {
      timeout = null;
      cb.apply(context, args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (!timeout) {cb.apply(context, args);}
  };
}

export const pluck = (array, key) => array.map((item) => item[key]);

// Workaround for files starting with "ad".
// Ad files are blocked by adblockers.
const applyAdBlockWorkaround = (countryCode) => {
  if (countryCode.toLowerCase() === 'ad.svg') {
    return 'andorra.svg';
  }
  return countryCode;
};

export const flagAsset = (flag) => {
  const flagIconName = (`${flag}.svg`).toLowerCase();
  return require(`@/assets/img/flags/${applyAdBlockWorkaround(flagIconName)}`);
};

export const sleep = (t = 1000) => new Promise(((resolve) => setTimeout(resolve, t)));

/*
  Convert strings to different cases.
  https://medium.com/javascript-in-plain-english/convert-string-to-different-case-styles-snake-kebab-camel-and-pascal-case-in-javascript-da724b7220d7
 */

/**
 * String to snake_case
 * @param {string} string
 * @returns {*}
 */
export function strSnakeCase(string) {
  return string
    .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    .map((x) => x.toLowerCase())
    .join('_');
}

/**
 * String to kebab-case
 * @param {string} string
 * @returns {*}
 */
export function strKebabCase(string) {
  return string
    .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    .map((x) => x.toLowerCase())
    .join('-');
}

/**
 * String to camelCase
 * @param {string} string
 * @returns {*}
 */
export function strCamelCase(string) {
  return string
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
}

/**
 * String to PascalCase
 * @param {string} string
 * @returns {*}
 */
export function strPascalCase(string) {
  return string
    .replace(/\w\S*/g, m.charAt(0).toUpperCase() + m.substr(1).toLowerCase());
}
